/**
 * Pure data helpers for the advanced learn-in flow (Nurse Call add device).
 *
 * Everything here is a plain function over `sp_getAdvLearnInDevices()` rows so it
 * can be verified without mounting a drawer or holding a session. The drawer that
 * consumes it is AdvancedLearnInDrawer.jsx; the design is
 * docs/ADVANCED_LEARN_IN_PLAN.md.
 *
 * The procedure returns one row per (type × use × model) because of its LEFT
 * JOINs onto the use and model entity tables, so the raw result is never directly
 * displayable — `foldCatalog` is the entry point.
 */

import { iAmHigherAuthority } from '../../../libs.jsx';

/** Manufacturer ids as they appear in the procedure's three UNION branches. */
export const INOVONICS_ID = 1;
export const PALCARE_ID = 4;
export const BECKLAR_ID = 7;

/** How many entries get the large image treatment before the list takes over. */
export const TILE_COUNT = 6;

/**
 * Role boundary for third-party hardware (plan §5a).
 *
 * `iAmHigherAuthority(80, w)` is `w >= 1000 || w > 80` — strictly greater than 80,
 * plus a super-admin escape. This matches what V1 has always done in its four
 * device-type pickers (Residents.jsx:629, Areas.jsx:578, and both
 * profile-templates), which V2's advanced drawer never carried over.
 *
 * If a role sitting at exactly weight 80 should be allowed to add third-party
 * devices, this is the one place to change: `parseInt(roleWeight, 10) >= 80`.
 */
export function canAddThirdParty(roleWeight) {
  return iAmHigherAuthority(80, roleWeight);
}

/**
 * Coerce a procedure flag to a real boolean.
 *
 * MySQL flag columns can surface as numbers or as strings depending on the
 * driver, and `Boolean('0')` is `true` — which would invert every gate in this
 * file. Going through Number() makes '0', 0, null and undefined all falsey while
 * leaving 1 and '1' truthy.
 */
export function flag(value) {
  if (value === true) return true;
  if (value === false || value == null || value === '') return false;
  return Number(value) === 1 || Number(value) > 0;
}

/** Composite catalog key. See the warning in the module header. */
export function entryKey(manufactureId, deviceTypeId) {
  return `${manufactureId}:${deviceTypeId}`;
}

/**
 * Coerce an id column, or null if it isn't one.
 *
 * `Number(null)` is 0 and `Number.isFinite(0)` is true, so a bare Number() +
 * isFinite check waves null straight through and mints a phantom `0:5` entry.
 * Null has to be rejected before the coercion, not after.
 */
function toId(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Fold raw procedure rows into one entry per (manufacturer, device type).
 *
 * Two things the JOIN shape forces:
 *
 *  - **A null use/model id means "this type has no such row"**, not "an option
 *    with id null". Those are dropped, so `uses`/`models` can legitimately end up
 *    empty and callers must cope.
 *  - **Zero is a real id, null is not.** The PalCare and Becklar branches hardcode
 *    `device_use_id: 0`, which is the correct saved value for those
 *    manufacturers, so the check has to be `== null` and never falsiness.
 *
 * @param {Array<object>} rows raw rows from fetchAdvLearnInDevices()
 * @returns {Array<object>} folded entries, unsorted and unfiltered
 */
export function foldCatalog(rows) {
  const byKey = new Map();

  (rows || []).forEach((row) => {
    if (row == null) return;
    const manufactureId = toId(row.manufacture_id);
    const deviceTypeId = toId(row.device_type_id);
    if (manufactureId == null || deviceTypeId == null) return;

    const key = entryKey(manufactureId, deviceTypeId);
    let entry = byKey.get(key);
    if (!entry) {
      entry = {
        key,
        manufacture_id: manufactureId,
        /* Shown as a quiet confirmation line once a device is picked. Optional —
           older procedure revisions didn't return it. */
        manufacture_name: row.manufacture_name || '',
        device_type_id: deviceTypeId,
        device_type_name: row.device_type_name || '',
        image_url: row.image_url || '',
        device_category_id: row.device_category_id == null ? null : Number(row.device_category_id),
        device_category_short_name: row.device_category_short_name || '',
        is_third_party: flag(row.is_third_party),
        is_area_device: flag(row.is_area_device),
        is_resident_device: flag(row.is_resident_device),
        /* Kept nullable on purpose: null vs 0 drives the sort (plan §3). */
        sort_order: row.sort_order == null ? null : Number(row.sort_order),
        uses: [],
        models: [],
      };
      byKey.set(key, entry);
    }

    if (row.device_use_id != null && !entry.uses.some((u) => u.device_use_id === Number(row.device_use_id))) {
      entry.uses.push({
        device_use_id: Number(row.device_use_id),
        option_number: row.option_number == null ? 0 : Number(row.option_number),
        option_text: row.option_text || '',
      });
    }

    if (row.model_id != null && !entry.models.some((m) => m.model_id === Number(row.model_id))) {
      entry.models.push({
        model_id: Number(row.model_id),
        model_name: row.model_name || '',
        /* Null for PalCare and Becklar — that's what suppresses the listener. */
        message_class: row.message_class == null ? null : Number(row.message_class),
        product_type_id: row.product_type_id == null ? null : Number(row.product_type_id),
      });
    }
  });

  /* Behaviors read best in the order the integrator numbered them. */
  byKey.forEach((entry) => {
    entry.uses.sort((a, b) => a.option_number - b.option_number);
  });

  return [...byKey.values()];
}

/**
 * Catalog order: non-null `sort_order` ascending, then null `sort_order`
 * alphabetically by name.
 *
 * The null test returns before any numeric compare, so nothing without a
 * `sort_order` can sort ahead of something with one — Becklar's 99 lands last
 * among the ordered entries but still above the alphabetical tail. The name
 * tiebreak inside an equal `sort_order` matters because the procedure gives every
 * PalCare row 1 and every Becklar row 99.
 */
export function byOrderThenName(a, b) {
  const aNull = a.sort_order == null;
  const bNull = b.sort_order == null;
  if (aNull !== bNull) return aNull ? 1 : -1;
  if (!aNull) {
    const diff = a.sort_order - b.sort_order;
    if (diff !== 0) return diff;
  }
  return (a.device_type_name || '').localeCompare(
    b.device_type_name || '', undefined, { sensitivity: 'base' },
  );
}

/**
 * The catalog for one base, role-filtered and ordered — what the picker renders.
 *
 * Third-party entries are omitted rather than disabled, so there is no route to a
 * picked row the user could not save (plan §5a).
 *
 * @param {Array<object>} entries folded entries
 * @param {'area'|'resident'} base
 * @param {number} roleWeight
 */
export function catalogForBase(entries, base, roleWeight) {
  const wantArea = base === 'area';
  const allowThirdParty = canAddThirdParty(roleWeight);
  return (entries || [])
    .filter((e) => (wantArea ? e.is_area_device : e.is_resident_device))
    .filter((e) => allowThirdParty || !e.is_third_party)
    .sort(byOrderThenName);
}

/** Split an ordered catalog into image tiles and the remaining list. */
export function splitTiles(entries, tileCount = TILE_COUNT) {
  return {
    tiles: (entries || []).slice(0, tileCount),
    rest: (entries || []).slice(tileCount),
  };
}

/**
 * Does this entry need the user to choose a behavior / model?
 *
 * One option is resolved silently — the whole point of the redesign. Zero options
 * is also nothing to ask about.
 */
export function needsUseChoice(entry) {
  return (entry?.uses?.length || 0) > 1;
}
export function needsModelChoice(entry) {
  return (entry?.models?.length || 0) > 1;
}

/**
 * The `device_use_id` to save.
 *
 * An Inovonics type with a single behavior still carries a **real** use id on its
 * `option_number: 0` row, so a lone option must contribute *its* id and never a
 * literal 0. Saving 0 is only correct where the procedure itself says 0, which is
 * the PalCare and Becklar branches. Getting this wrong is the bug that shipped in
 * the first cut of the easy learn-in drawer.
 *
 * @param {object} entry folded entry
 * @param {number|string|null} chosenUseId what the user picked, if anything
 */
export function resolveUseId(entry, chosenUseId = null) {
  const uses = entry?.uses || [];
  if (chosenUseId != null && chosenUseId !== '') {
    const chosen = uses.find((u) => String(u.device_use_id) === String(chosenUseId));
    if (chosen) return chosen.device_use_id;
  }
  if (uses.length > 0) return uses[0].device_use_id;
  return 0;
}

/**
 * The model to save and to listen with.
 *
 * Returns the full model because the listener needs `product_type_id` and
 * `message_class` off it, not just the id.
 */
export function resolveModel(entry, chosenModelId = null) {
  const models = entry?.models || [];
  if (chosenModelId != null && chosenModelId !== '') {
    const chosen = models.find((m) => String(m.model_id) === String(chosenModelId));
    if (chosen) return chosen;
  }
  return models[0] || null;
}

/**
 * Can this device be learned in by listening?
 *
 * Needs a resolved model carrying a real `product_type_id`. PalCare and Becklar
 * rows return null there, which is exactly why neither supports the listener —
 * no manufacturer special-casing required.
 */
export function canListen(entry, model) {
  if (!entry || !model) return false;
  return Number(model.product_type_id) > 0;
}

// ---------------------------------------------------------------------------
// Assignment target options
//
// Built here rather than in the drawer so the ordering is testable, and shaped
// for SearchableSelect ({ value, label, searchText }) — the same component and
// option shape the Add Resident drawer uses for its area picker
// (residents/components/ResidentDrawer.jsx:64-72).
// ---------------------------------------------------------------------------

/** Case-insensitive, numeric-aware: "Room 2" sorts before "Room 10". */
function compareNames(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

/**
 * A resident's display name.
 *
 * `sp_dev_getResidentsByCampusID` returns a composed `resident_name` as well as
 * the parts; the first/last fallback covers any caller passing rows from a
 * different query.
 */
export function residentLabel(r) {
  const composed = `${r?.resident_first_name || ''} ${r?.resident_last_name || ''}`.trim();
  return r?.resident_name || composed || `Resident ${r?.resident_id ?? ''}`.trim();
}

/**
 * Sort key for residents: first name when the row carries one, otherwise the
 * composed name.
 *
 * `sp_dev_getResidentsByCampusID` does return `resident_first_name`, so that is
 * the live path; the fallback only covers rows from another query.
 */
function residentSortKey(r) {
  return r?.resident_first_name || r?.resident_name || '';
}

/** Areas as select options, sorted by name ascending. */
export function areaOptions(areas) {
  return [...(areas || [])]
    .sort((a, b) => compareNames(a.area_name, b.area_name))
    .map((a) => ({
      value: String(a.area_id),
      label: a.area_type_name ? `${a.area_name} (${a.area_type_name})` : String(a.area_name || ''),
      /* Search on the raw fields so typing a room number or wing matches
         regardless of how the label was composed. */
      searchText: `${a.area_name || ''} ${a.area_type_name || ''}`.trim(),
    }));
}

/** Residents as select options, sorted by first name (see residentSortKey). */
export function residentOptions(residents) {
  return [...(residents || [])]
    .sort((a, b) => compareNames(residentSortKey(a), residentSortKey(b)))
    .map((r) => ({
      value: String(r.resident_id),
      label: residentLabel(r),
      searchText: `${residentLabel(r)} ${r.area_name || ''}`.trim(),
    }));
}

// ---------------------------------------------------------------------------
// Serial numbers
//
// Rules carried over verbatim from AddDeviceDrawer.jsx (:24-45, :475-505) so the
// replacement flow accepts and stores exactly what the old one did. Lifted here
// to be shared and testable rather than re-derived.
// ---------------------------------------------------------------------------

/** Inovonics transmitter ids are integers in this inclusive range. */
export const SERIAL_MIN = 1;
export const SERIAL_MAX = 16777216;

/** Inovonics serials are additionally required to be at least this long. */
export const INOVONICS_SERIAL_MIN_LENGTH = 4;

/**
 * Normalize a serial for storage.
 *
 * Two manufacturer-specific rules:
 *  - **PalCare device type 1** is a MAC-style address, stored lowercase with `-`
 *    and `:` stripped, so `AA:BB-CC` and `aabbcc` are the same device.
 *  - **All-numeric serials drop leading zeros**, so `000123` and `123` don't
 *    become two records. `'0'` stays `'0'` rather than collapsing to empty.
 */
export function normalizeSerial(raw, entry) {
  let s = String(raw || '').trim();
  if (!s) return '';
  if (entry?.manufacture_id === PALCARE_ID && Number(entry?.device_type_id) === 1) {
    return s.toLowerCase().replaceAll('-', '').replaceAll(':', '');
  }
  if (/^\d+$/.test(s)) {
    s = s.replace(/^0+/, '');
    if (s === '') s = '0';
  }
  return s;
}

/**
 * Validate a serial for the picked device. Returns '' when acceptable.
 *
 * Inovonics is numeric and range-bounded; everything else is alphanumeric. The
 * range check runs on the normalized value so `0000123` is judged as `123`.
 */
export function validateSerial(raw, entry) {
  const s = normalizeSerial(raw, entry);
  if (!s) return 'Enter the serial number.';

  if (entry?.manufacture_id === INOVONICS_ID) {
    if (!/^\d+$/.test(s)) return 'Inovonics serials are numbers only.';
    if (s.length < INOVONICS_SERIAL_MIN_LENGTH) {
      return `Serial must be at least ${INOVONICS_SERIAL_MIN_LENGTH} digits.`;
    }
    const n = Number(s);
    if (!Number.isFinite(n) || n < SERIAL_MIN || n > SERIAL_MAX) {
      return `Serial must be between ${SERIAL_MIN} and ${SERIAL_MAX}.`;
    }
    return '';
  }

  if (!/^[A-Za-z0-9]+$/.test(s)) return 'Serial must be letters and numbers only.';
  return '';
}

/**
 * Where is this existing device currently assigned?
 *
 * Device rows carry a `residents` array (DevicesTable.jsx:134-141) and a flat
 * `area_name`, so both shapes have to be read.
 *
 * @returns {{ kind: 'resident'|'area'|'none', label: string }}
 */
export function describeAssignment(device) {
  const names = (device?.residents || []).map((r) => r?.resident_name).filter(Boolean);
  if (names.length) return { kind: 'resident', label: names.join(', ') };
  if (device?.area_name) return { kind: 'area', label: String(device.area_name) };
  return { kind: 'none', label: '' };
}

/**
 * Is the existing device already on the target the user picked?
 *
 * Reassigning something to where it already is should be a no-op, not a pointless
 * write that logs an audit entry and churns the vendor integrations.
 */
export function isAlreadyOnTarget(device, base, targetId) {
  if (!device || targetId == null || String(targetId) === '') return false;
  if (base === 'area') {
    const areaId = device.area_id;
    /* 0 / null means unassigned, which is never "already on" a real area. */
    if (!areaId) return false;
    return String(areaId) === String(targetId);
  }
  if ((device.residents || []).some((r) => String(r?.resident_id) === String(targetId))) return true;
  const residentId = device.resident_id;
  if (!residentId) return false;
  return String(residentId) === String(targetId);
}

/**
 * Does the existing device match the type the user picked?
 *
 * A mismatch means the typed serial belongs to something else entirely — a
 * pendant when they picked a ceiling cord — so confirming would quietly move the
 * wrong device. Returns true when it can't tell, to avoid crying wolf on rows
 * that don't carry a type.
 */
export function isSameDeviceType(device, entry) {
  if (!device || !entry) return true;
  if (device.device_type_id == null) return true;
  if (String(device.device_type_id) !== String(entry.device_type_id)) return false;
  if (device.manufacture_id == null) return true;
  return String(device.manufacture_id) === String(entry.manufacture_id);
}

/**
 * Find a campus device already using this serial.
 *
 * Compares as string and, when both sides parse, as number — an existing record
 * may hold `00123` where the user typed `123`. Same rule as the easy learn-in
 * drawer so the two flows agree on what counts as a duplicate.
 */
export function findExistingDevice(devices, raw, entry) {
  const s = normalizeSerial(raw, entry);
  if (!s) return null;
  const asNum = Number(s);
  return (devices || []).find((d) => {
    const uuid = d?.manufacture_uuid;
    if (uuid == null) return false;
    const existing = String(uuid).trim();
    if (existing === s) return true;
    if (existing.toLowerCase() === s.toLowerCase()) return true;
    return Number.isFinite(asNum) && Number(existing) === asNum;
  }) || null;
}
